import { TeamRulesStore } from './team-rules/store';
import { FieldState, FormState } from 'formstate';
import {
    ChallengeActivityFilterFormValue,
    challengeActivityFiltersFromDto
} from './activity-filters';
import { buildChallengeRulePayload } from './rule-payload';
import { isRawValueRuleType } from '../../rule-types';
import { component } from 'tsdi';
import { action, computed, makeObservable, observable, reaction } from 'mobx';
import { wrapInFormField } from '@core/forms';
import { injectTSDI } from '@core/tsdi';
import { wrapRequest } from 'wrap-request';
import { autodisposer } from '@core/reactions';
import { required } from '@components/utils/validators';
import { ChallengeDataStore } from '../data-store';
import {
    ActivityType,
    ChallengeRuleType,
    ChallengeAggregationType,
    ChallengeFitnessMetricType,
    ChallengeInitialResultMode,
    ChallengeNormalizationOperation,
    ChallengeNormalizationOutputFormat,
    ChallengeStatus
} from '@core/api/ChallengeClient';
import type {
    ChallengeDisplayNormalizationFormulaDto,
    ChallengeRuleConfigDto,
    ChallengeRuleConfigUpdateDto
} from '@core/api/ChallengeClient';
import {
    formErrors,
    isFormStateDirty,
    makeFormClean
} from '@components/utils/form';
import { StepStatus } from '../store';
import { bind } from 'lodash-decorators';
import { toZonedDateTime, ZonedDateTime } from '@ms-rewards/date-lib';

import {
    extractTimeZoneFromZonedString,
    truncateDateToMidnight
} from '@components/utils/time';
import { Api } from '@core/api';
import { DEFAULT_TIME_ZONE } from '@components/utils/time';
import { PersonalRulesStore } from '@src/communities/community/challenges/challenge-form/rules/personal-rules/store';
import { GroupRulesStore } from '@src/communities/community/challenges/challenge-form/rules/group-rules/store';
import {
    CompositePointDefinitionDto,
    WorkoutCalculatedMetricDefinitionDto,
    WorkoutCategory,
    WorkoutMetricAlgorithm
} from '@api/FitnessActivityClient';

const displayFormulaByAlgorithm: Record<
    WorkoutMetricAlgorithm,
    ChallengeDisplayNormalizationFormulaDto
> = {
    [WorkoutMetricAlgorithm.BEST_EFFORT]: {
        operation: ChallengeNormalizationOperation.MULTIPLY,
        operand: 1,
        outputFormat: ChallengeNormalizationOutputFormat.DURATION
    }
};

export type ChallengeRulesFormType = ReturnType<
    ChallengeRulesStore['generateForm']
>;

export const DEFAULT_CHALLENGE_TIME = {
    start: { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 },
    end: { hours: 23, minutes: 59, seconds: 59, milliseconds: 999 }
};

@component
export class ChallengeRulesStore {
    constructor() {
        this.form = this.generateForm(
            this.challengeDataStore.challengeConfiguration?.rule
        );

        makeObservable(this, {
            form: observable,
            state: observable,
            isActive: observable,
            compositePointDefinitions: observable,
            calculatedMetricDefinitions: observable,
            fields: computed,
            errors: computed,
            onCollapsedToggle: action.bound,
            openStep: action.bound,
            closeStep: action.bound,
            selectCalculatedMetric: action.bound
        });
    }
    private teamRulesStore = injectTSDI(TeamRulesStore);
    private personalRulesStore = injectTSDI(PersonalRulesStore);
    private groupRulesStore = injectTSDI(GroupRulesStore);

    private get challengeClient() {
        return injectTSDI(Api).challengeClient;
    }

    private get fitnessActivityClient() {
        return injectTSDI(Api).fitnessActivityClient;
    }

    private get challengeDataStore() {
        return injectTSDI(ChallengeDataStore);
    }

    public form: ChallengeRulesFormType;
    public state: StepStatus = StepStatus.CREATING;
    public isActive: boolean = false;
    public compositePointDefinitions: CompositePointDefinitionDto[] = [];
    public calculatedMetricDefinitions: WorkoutCalculatedMetricDefinitionDto[] =
        [];

    private generateForm(
        configuration?: ChallengeRuleConfigDto,
        autoJoinEnabled?: boolean
    ) {
        const complete =
            configuration?.personalRuleSet || configuration?.teamRuleSet;

        this.state = complete ? StepStatus.COMPLETED : StepStatus.CREATING;

        const initialZone =
            extractTimeZoneFromZonedString(configuration?.dateTimeRange.from) ||
            DEFAULT_TIME_ZONE;

        return new FormState({
            timeZone: new FieldState<string>(initialZone),
            timeFrom: new FieldState<ZonedDateTime | undefined>(
                configuration?.dateTimeRange.from
                    ? toZonedDateTime(
                          configuration?.dateTimeRange.from,
                          initialZone
                      )
                    : undefined
            ).validators(this.futureDateValidator, required()),
            timeTo: new FieldState<ZonedDateTime | undefined>(
                configuration?.dateTimeRange.to
                    ? toZonedDateTime(
                          configuration?.dateTimeRange.to,
                          initialZone
                      )
                    : undefined
            ).validators(this.futureDateValidator, required()),
            joinDeadline: new FieldState<ZonedDateTime | undefined>(
                configuration?.joinDeadline
                    ? toZonedDateTime(configuration?.joinDeadline, initialZone)
                    : undefined
            ),
            activityType: new FieldState<ActivityType | undefined>(
                configuration?.activityType || undefined
            ),
            workoutCategory: new FieldState<WorkoutCategory | undefined>(
                configuration?.workoutCategory || undefined
            ),
            useCompositePoints: new FieldState<boolean>(
                Boolean(configuration?.compositePointTypeKey)
            ),
            compositePointTypeKey: new FieldState<string | undefined>(
                configuration?.compositePointTypeKey || undefined
            ).validators(this.compositePointTypeValidator),
            ruleType: new FieldState<ChallengeRuleType>(
                configuration?.ruleType || ChallengeRuleType.ACTIVITY_POINTS
            ),
            fitnessMetricType: new FieldState<ChallengeFitnessMetricType>(
                configuration?.fitnessMetricType ||
                    ChallengeFitnessMetricType.PACE
            ),
            calculatedMetricKey: new FieldState<string | undefined>(
                configuration?.calculatedMetricKey || undefined
            ).validators(this.calculatedMetricKeyValidator),
            aggregationType: new FieldState<ChallengeAggregationType>(
                configuration?.aggregationType || ChallengeAggregationType.SUM
            ),
            initialResultMode: new FieldState<ChallengeInitialResultMode>(
                configuration?.initialResultMode ||
                    ChallengeInitialResultMode.CURRENT_DAY_ONLY
            ),
            resultBaseline: new FieldState<number | undefined>(
                configuration?.resultBaseline
            ),
            normalizationOperation: new FieldState<
                ChallengeNormalizationOperation | undefined
            >(configuration?.displayNormalizationFormula?.operation),
            normalizationOperand: new FieldState<number | undefined>(
                configuration?.displayNormalizationFormula?.operand
            ).validators(this.normalizationOperandValidator),
            normalizationOutputFormat:
                new FieldState<ChallengeNormalizationOutputFormat>(
                    configuration?.displayNormalizationFormula?.outputFormat ||
                        ChallengeNormalizationOutputFormat.DURATION
                ),
            activityFilters: new FieldState<ChallengeActivityFilterFormValue[]>(
                challengeActivityFiltersFromDto(configuration?.activityFilters)
            ),
            autoJoinEnabled: new FieldState<boolean>(autoJoinEnabled ?? false),

            teamRules: new FieldState<boolean>(
                Boolean(configuration?.teamRuleSet)
            ).validators(this.teamRulesValidator),
            groupRules: new FieldState<boolean>(
                Boolean(
                    configuration?.groupRules &&
                        configuration.groupRules.length > 0
                )
            ).validators(this.groupRulesValidator)
        });
    }

    @bind
    private async teamRulesValidator(): Promise<string | false> {
        if (!this.fields.teamRules.value) {
            return false;
        } else {
            const teamRulesValidation =
                await this.teamRulesStore.form.validate();
            if (teamRulesValidation.hasError) {
                return 'Fix team rules';
            }
            return false;
        }
    }
    @bind
    private async personalRulesValidator(): Promise<string | false> {
        const personalRulesValidation =
            await this.personalRulesStore.form.validate();
        if (personalRulesValidation.hasError) {
            return 'Fix personal rules';
        }
        return false;
    }
    @bind
    private async groupRulesValidator(): Promise<string | false> {
        if (!this.fields.groupRules.value) {
            return false;
        } else {
            const groupRulesValidation =
                await this.groupRulesStore.form.validate();
            const individualRulesValidation = await Promise.all(
                this.groupRulesStore.groupRulesList.map((rule) =>
                    rule.validate()
                )
            );
            if (
                groupRulesValidation.hasError ||
                individualRulesValidation.some((v) => v.hasError)
            ) {
                return 'Fix group rules';
            }
            return false;
        }
    }

    @bind
    private futureDateValidator(
        val: ZonedDateTime | undefined,
        timezone = DEFAULT_TIME_ZONE
    ): string | false {
        if (this.challengeDataStore.disabledRulesEdit) {
            return false;
        } else if (!val) {
            return 'Start / End dates';
        }
        val = truncateDateToMidnight(val);
        const today = truncateDateToMidnight(
            toZonedDateTime(new Date(), timezone).setHour(12).subDays(1)
        ).toDate();

        if (val?.isBefore(today)) {
            return 'cp.challenge.field.start.end.date.error';
        }

        return false;
    }

    @bind
    private normalizationOperandValidator(
        val: number | undefined
    ): string | false {
        if (!this.form?.$.normalizationOperation?.value) {
            return false;
        }
        if (val === undefined || val <= 0) {
            return 'Enter a multiplier greater than 0';
        }
        return false;
    }

    @bind
    private compositePointTypeValidator(
        val: string | undefined
    ): string | false {
        if (this.form?.$.useCompositePoints?.value && !val) {
            return 'Select a composite point type';
        }
        return false;
    }

    @bind
    private calculatedMetricKeyValidator(
        val: string | undefined
    ): string | false {
        if (
            isRawValueRuleType(this.form?.$.ruleType?.value) &&
            this.form?.$.fitnessMetricType?.value ===
                ChallengeFitnessMetricType.WORKOUT_CALCULATED_METRIC &&
            !val
        ) {
            return 'Select a calculated metric';
        }
        return false;
    }

    public selectCalculatedMetric(metricKey: string | undefined) {
        this.form.$.calculatedMetricKey.onChange(metricKey);
        const definition = this.calculatedMetricDefinitions.find(
            (candidate) => candidate.metricKey === metricKey
        );
        const formula =
            definition && displayFormulaByAlgorithm[definition.algorithm];
        if (!formula) {
            return;
        }
        this.form.$.normalizationOperation.onChange(formula.operation);
        this.form.$.normalizationOperand.onChange(formula.operand);
        this.form.$.normalizationOutputFormat.onChange(formula.outputFormat);
    }

    public get errors() {
        return formErrors(this.form);
    }

    public get formIsDirty() {
        return (
            isFormStateDirty(this.form.$) ||
            this.teamRulesStore.formIsDirty ||
            this.groupRulesStore.formIsDirty
        );
    }
    public get fields() {
        return {
            activityType: wrapInFormField(this.form.$.activityType),
            timeZone: wrapInFormField(this.form.$.timeZone),
            timeFrom: wrapInFormField(this.form.$.timeFrom),
            timeTo: wrapInFormField(this.form.$.timeTo),
            joinDeadline: wrapInFormField(this.form.$.joinDeadline),
            autoJoinEnabled: wrapInFormField(this.form.$.autoJoinEnabled),
            teamRules: wrapInFormField(this.form.$.teamRules),
            groupRules: wrapInFormField(this.form.$.groupRules),
            workoutCategory: wrapInFormField(this.form.$.workoutCategory),
            useCompositePoints: wrapInFormField(this.form.$.useCompositePoints),
            compositePointTypeKey: wrapInFormField(
                this.form.$.compositePointTypeKey
            ),
            ruleType: wrapInFormField(this.form.$.ruleType),
            fitnessMetricType: wrapInFormField(this.form.$.fitnessMetricType),
            calculatedMetricKey: wrapInFormField(
                this.form.$.calculatedMetricKey
            ),
            aggregationType: wrapInFormField(this.form.$.aggregationType),
            initialResultMode: wrapInFormField(this.form.$.initialResultMode),
            resultBaseline: wrapInFormField(this.form.$.resultBaseline),
            normalizationOperation: wrapInFormField(
                this.form.$.normalizationOperation
            ),
            normalizationOperand: wrapInFormField(
                this.form.$.normalizationOperand
            ),
            normalizationOutputFormat: wrapInFormField(
                this.form.$.normalizationOutputFormat
            ),
            activityFilters: wrapInFormField(this.form.$.activityFilters)
        };
    }

    public compositePointDefinitionsRequest = wrapRequest(
        async (communityId: number) => {
            const result =
                await this.fitnessActivityClient?.admin.compositePointDefinitionControllerGetByCommunityId(
                    { communityId }
                );
            this.compositePointDefinitions = result?.data ?? [];
            return result?.data;
        }
    );

    public calculatedMetricDefinitionsRequest = wrapRequest(async () => {
        const result =
            await this.fitnessActivityClient?.admin.workoutCalculatedMetricDefinitionControllerGetAll();
        this.calculatedMetricDefinitions = (result?.data ?? []).filter(
            (definition) => definition.enabled
        );
        return result?.data;
    });

    public challengeRulesRequest = wrapRequest(
        async ({
            challengeId,
            data
        }: {
            challengeId: number;
            data: ChallengeRuleConfigUpdateDto;
        }) => {
            const result =
                await this.challengeClient?.support.challengeSupportControllerUpdateRules(
                    challengeId,
                    data
                );

            return result?.data;
        }
    );

    @bind
    public async validateRules(onErrorOnly: boolean = false) {
        if (onErrorOnly) {
            if (this.state === StepStatus.ERROR) {
                const validation = await this.form.validate();
                return this.validateAll(validation.hasError);
            }
            this.state = StepStatus.MODIFIED;
            return false;
        }
        const validation = await this.form.validate();
        return this.validateAll(validation.hasError);
    }
    private async validateAll(hasError: boolean) {
        const teamRulesValidation = await this.teamRulesValidator();
        const personalRulesValidation = await this.personalRulesValidator();
        const groupRulesValidation = await this.groupRulesValidator();

        if (
            hasError ||
            teamRulesValidation ||
            personalRulesValidation ||
            groupRulesValidation
        ) {
            this.state = StepStatus.ERROR;
            return true;
        } else if (
            this.challengeDataStore.challengeConfiguration === undefined ||
            this.state === StepStatus.CREATING
        ) {
            this.state = StepStatus.CREATING;
            return false;
        } else if (
            this.formIsDirty ||
            this.teamRulesStore.formIsDirty ||
            this.groupRulesStore.formIsDirty ||
            this.personalRulesStore.formIsDirty
        ) {
            this.state = StepStatus.MODIFIED;
            return false;
        }

        this.state = StepStatus.COMPLETED;
        makeFormClean(this.form);
        return false;
    }

    get rulesAreDirty(): boolean {
        return (
            this.formIsDirty ||
            this.teamRulesStore.formIsDirty ||
            this.groupRulesStore.formIsDirty ||
            this.personalRulesStore.formIsDirty
        );
    }

    @bind
    public async updateChallengeRules() {
        const validation = await this.validateRules();

        if (validation) {
            this.state = StepStatus.ERROR;
        } else if (this.challengeDataStore.challengeId && this.rulesAreDirty) {
            this.state = StepStatus.UPDATING;
            if (
                this.challengeDataStore.challengeStatus ===
                ChallengeStatus.PUBLISHED
            ) {
                await this.challengeRulesRequest.request({
                    challengeId: this.challengeDataStore.challengeId,
                    data: this.generateRequestUpdateRulesData(this.form)
                });
            } else if (
                this.challengeDataStore.challengeStatus ===
                    ChallengeStatus.IN_PROGRESS &&
                this.groupRulesStore.formIsDirty
            ) {
                await this.groupRulesStore.updateGroupRules();
            }
        }
    }

    public onCollapsedToggle() {
        this.isActive = !this.isActive;
    }

    public openStep() {
        this.isActive = true;
    }

    public closeStep() {
        this.isActive = false;
    }

    public generateRequestUpdateRulesData(
        form: ChallengeRulesFormType
    ): ChallengeRuleConfigUpdateDto {
        const personalRuleSet = this.personalRulesStore.generateRequestData(
            this.personalRulesStore.form
        );
        const teamRuleSet = this.teamRulesStore.form.$.teamRulesEnabled.value
            ? this.teamRulesStore.generateRequestData(this.teamRulesStore.form)
            : undefined;
        const groupRules = this.groupRulesStore.generateRequestData(
            this.groupRulesStore.form
        );

        return {
            dateTimeRange: {
                from: form.$.timeFrom.value?.toJSON() || '',
                to: form.$.timeTo.value?.toJSON() || ''
            },
            ...buildChallengeRulePayload(form),
            joinDeadline: form.$.joinDeadline.value?.toJSON(),
            recordAttempts:
                this.challengeDataStore.challengeConfiguration?.rule
                    .recordAttempts ?? false,
            personalRuleSet: personalRuleSet,
            teamRuleSet: teamRuleSet,
            groupRules: groupRules
        };
    }

    @autodisposer.tsdi
    public initialize() {
        this.calculatedMetricDefinitionsRequest.request();
        return [
            reaction(
                () => this.challengeDataStore.challengeConfiguration,
                async (configuration) => {
                    this.form = this.generateForm(
                        configuration?.rule,
                        configuration?.autoJoinEnabled
                    );
                }
            ),
            reaction(
                () => this.challengeDataStore.communityId,
                (communityId) => {
                    if (communityId) {
                        this.compositePointDefinitionsRequest.request(
                            communityId
                        );
                    }
                },
                { fireImmediately: true }
            )
        ];
    }
}
